import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronDown, Download, FileSpreadsheet, FileText } from 'lucide-react';
import { reportsService, ExportParams } from '../../services/reports';
import { Spinner } from '@/components/ui';

interface ExportButtonProps {
  reportType: string;
  fromDate: string;
  toDate: string;
  disabled?: boolean;
}

export function ExportButton({ reportType, fromDate, toDate, disabled = false }: ExportButtonProps) {
  const { t } = useTranslation();
  const [isExporting, setIsExporting] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const rootRef = useRef<HTMLDivElement>(null);

  // Close the menu on outside click / Escape.
  useEffect(() => {
    if (!showDropdown) return;
    const onPointer = (e: MouseEvent) => {
      if (!rootRef.current?.contains(e.target as Node)) setShowDropdown(false);
    };
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && setShowDropdown(false);
    document.addEventListener('mousedown', onPointer);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onPointer);
      document.removeEventListener('keydown', onKey);
    };
  }, [showDropdown]);

  // Auto-dismiss the error after a few seconds.
  useEffect(() => {
    if (!error) return;
    const timer = window.setTimeout(() => setError(null), 6000);
    return () => window.clearTimeout(timer);
  }, [error]);

  const handleExport = async (format: 'csv' | 'pdf') => {
    setIsExporting(true);
    setShowDropdown(false);
    setError(null);

    try {
      const params: ExportParams = { reportType, fromDate, toDate, format };
      const blob = await reportsService.exportReport(params);

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${reportType}-report-${fromDate}-to-${toDate}.${format}`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Export failed:', err);
      setError(t('charts.export.failed'));
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <div ref={rootRef} className="relative">
      <button
        type="button"
        onClick={() => setShowDropdown((v) => !v)}
        disabled={isExporting || disabled}
        aria-haspopup="menu"
        aria-expanded={showDropdown}
        className="btn-secondary"
      >
        {isExporting ? (
          <>
            <Spinner className="h-4 w-4" />
            {t('charts.export.exporting')}
          </>
        ) : (
          <>
            <Download className="h-4 w-4" />
            {t('charts.export.button')}
            <ChevronDown className="h-4 w-4 text-gray-400" />
          </>
        )}
      </button>

      {showDropdown && (
        <div
          role="menu"
          className="absolute end-0 z-20 mt-2 w-52 overflow-hidden rounded-lg border border-gray-200 bg-white py-1 shadow-card-hover"
        >
          <button
            type="button"
            role="menuitem"
            onClick={() => handleExport('csv')}
            className="flex w-full items-center gap-2 px-4 py-2 text-start text-sm text-gray-700 hover:bg-gray-50"
          >
            <FileSpreadsheet className="h-4 w-4 text-success-600" />
            {t('charts.export.csv')}
          </button>
          <button
            type="button"
            role="menuitem"
            onClick={() => handleExport('pdf')}
            className="flex w-full items-center gap-2 px-4 py-2 text-start text-sm text-gray-700 hover:bg-gray-50"
          >
            <FileText className="h-4 w-4 text-danger-600" />
            {t('charts.export.pdf')}
          </button>
        </div>
      )}

      {error && (
        <p role="alert" className="alert-danger absolute end-0 z-20 mt-2 w-64 p-3 text-xs">
          {error}
        </p>
      )}
    </div>
  );
}
